import "@/assets/css/vendors/simplebar.css";
import "@/assets/css/themes/echo.css";
import { Transition } from "react-transition-group";
import Breadcrumb from "@/components/Base/Breadcrumb";
import { useState, useEffect, createRef, useRef, useMemo, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { selectSideMenu } from "@/stores/sideMenuSlice";
import {
  selectCompactMenu,
  setCompactMenu as setCompactMenuStore,
} from "@/stores/compactMenuSlice";
import { useAppDispatch, useAppSelector } from "@/stores/hooks";
import { FormattedMenu, linkTo, nestedMenu, enter, leave } from "./side-menu";
import Lucide from "@/components/Base/Lucide";
// import users from "@/fakers/users"; // Disabled faker profiles
import clsx from "clsx";
import SimpleBar from "simplebar";
import { Menu } from "@/components/Base/Headless";
import SwitchAccount from "@/components/SwitchAccount";
import NotificationsPanel from "@/components/NotificationsPanel";
import ActivitiesPanel from "@/components/ActivitiesPanel";
import { useBreadcrumbs} from "@/hooks/useBreadcrumbs";
import { selectSideProjectMenu, setProjectId, setProjectName, resetProjectMenu } from "@/stores/sideProjectMenuSlice";
import { useProjectContext } from "@/hooks/useProjectContext";
import { getToken } from "@/utils/auth";

// Helper function to get user initials
function getUserInitials(firstName?: string, lastName?: string): string {
  if (!firstName && !lastName) return "U";
  const first = firstName?.charAt(0).toUpperCase() || "";
  const last = lastName?.charAt(0).toUpperCase() || "";
  return first + last || "U";
}

function Main() {
  const dispatch = useAppDispatch();
  const compactMenu = useAppSelector(selectCompactMenu);
  const setCompactMenu = (val: boolean) => {
    localStorage.setItem("compactMenu", val.toString());
    dispatch(setCompactMenuStore(val));
  };
  const [switchAccount, setSwitchAccount] = useState(false);
  const [notificationsPanel, setNotificationsPanel] = useState(false);
  const [activitiesPanel, setActivitiesPanel] = useState(false);
  const [compactMenuOnHover, setCompactMenuOnHover] = useState(false);
  const [activeMobileMenu, setActiveMobileMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [formattedMenu, setFormattedMenu] = useState<Array<FormattedMenu | string>>([]);
  const breadcrumbs = useBreadcrumbs();
  const projectContext = useProjectContext();
  const [userInitials, setUserInitials] = useState<string>("U");
  
  // Get user initials from firstName and lastName by fetching user data
  useEffect(() => {
    const getInitials = async () => {
      try {
        const token = getToken();
        if (!token) {
          return;
        }

        // Decode JWT token to get userId
        let userId: string | null = null;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.userId || null;
        } catch (e) {
          console.error("Failed to decode JWT token:", e);
          return;
        }

        if (!userId) {
          return;
        }

        // Fetch user data using userId to get firstName and lastName
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
        const res = await fetch(`${apiUrl}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const userData = await res.json();
          // Extract firstName and lastName from the response
          const firstName = userData?.data?.firstName || userData?.firstName;
          const lastName = userData?.data?.lastName || userData?.lastName;
          const initials = getUserInitials(firstName, lastName);
          setUserInitials(initials);
        } else {
          console.error("Failed to fetch user data:", res.status);
        }
      } catch (error) {
        console.error("Failed to get user data:", error);
        // Keep default "U" if all methods fail
      }
    };
    getInitials();
  }, []);
  
  // ✅ Memoized menu computation
  const sideMenuStore = useAppSelector(selectSideMenu);
  const sideProjectMenuStore = useAppSelector(selectSideProjectMenu);

  const activeMenuStore = useMemo(
    () => (projectContext.isInProject ? sideProjectMenuStore : sideMenuStore),
    [projectContext.isInProject, sideProjectMenuStore, sideMenuStore]
  );

  const sideMenu = useCallback(() => nestedMenu(activeMenuStore, location), [
    activeMenuStore,
    location,
  ]);

  useEffect(() => {
    setFormattedMenu(sideMenu());
  }, [sideMenu]);

  
  const simpleBarInstanceRef = useRef<SimpleBar | null>(null);


  const scrollableRef = createRef<HTMLDivElement>();

  const [topBarActive, setTopBarActive] = useState(false);

  const hideSideMenuOnClients =
    location.pathname === "/clients" && !location.search;

  const toggleCompactMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setCompactMenu(!compactMenu);
  };

  const compactLayout = () => {
    if (window.innerWidth <= 1600) {
      setCompactMenu(true);
    }
  };

  const requestFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  };

  useEffect(() => {
    if (!projectContext || projectContext.isInProject === undefined) return;

    // ✅ Initialize SimpleBar only once
    if (scrollableRef.current && !simpleBarInstanceRef.current) {
      simpleBarInstanceRef.current = new SimpleBar(scrollableRef.current);
    }

    compactLayout();

    // ✅ Clean up when component unmounts
    return () => {
      if (simpleBarInstanceRef.current) {
        // @ts-ignore: SimpleBar internal method
        simpleBarInstanceRef.current.unMount?.();
        simpleBarInstanceRef.current = null;
      }
    };
  }, [projectContext.isInProject]);


  // Update project menu with project ID and name when in project context
  useEffect(() => {
    if (projectContext.isInProject && projectContext.projectId) {
      dispatch(setProjectId(projectContext.projectId));
      // Update project name in menu if available
      if (projectContext.projectName) {
        dispatch(setProjectName(projectContext.projectName));
      }
    } else if (!projectContext.isInProject) {
      // Reset to global menu when not in project context
      dispatch(resetProjectMenu());
    }
  }, [projectContext.isInProject, projectContext.projectId, projectContext.projectName, dispatch]);

  useEffect(() => {
    let ticking = false;
    let lastState = topBarActive;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const active =
          document.body.scrollTop > 0 || document.documentElement.scrollTop > 0;
        if (active !== lastState) {
          setTopBarActive(active);
          lastState = active;
        }
        ticking = false;
      });
    };

    const handleResize = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        compactLayout();
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    // Initial call
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);


  return (
    <div
      className={clsx([
        "echo group bg-gradient-to-b from-slate-200/70 to-slate-50 background relative min-h-screen dark:from-darkmode-800/[.95] dark:to-darkmode-900/[.95]",
        "before:content-[''] before:h-[370px] before:w-screen before:bg-gradient-to-t before:from-theme-1/80 before:to-theme-2 [&.background--hidden]:before:opacity-0 before:transition-[opacity,height] before:ease-in-out before:duration-300 before:top-0 before:fixed",
        "after:content-[''] after:h-[370px] after:w-screen [&.background--hidden]:after:opacity-0 after:transition-[opacity,height] after:ease-in-out after:duration-300 after:top-0 after:fixed after:bg-texture-white after:bg-contain after:bg-fixed after:bg-[center_-13rem] after:bg-no-repeat",
        topBarActive && "background--hidden",
      ])}
    >
      {!hideSideMenuOnClients && projectContext.isInProject !== undefined && (
      <div
        className={clsx([
          "xl:ml-0 shadow-xl transition-[margin,padding] duration-300 xl:shadow-none fixed top-0 left-0 z-50 side-menu group inset-y-0 xl:py-3.5 xl:pl-3.5",
          "after:content-[''] after:fixed after:inset-0 after:bg-black/80 after:xl:hidden",
          { "side-menu--collapsed": compactMenu },
          { "side-menu--on-hover": compactMenuOnHover },
          { "ml-0 after:block": activeMobileMenu },
          { "-ml-[275px] after:hidden": !activeMobileMenu },
        ])}
      >
        <div
          className={clsx([
            "fixed ml-[275px] w-10 h-10 items-center justify-center xl:hidden z-50",
            { flex: activeMobileMenu },
            { hidden: !activeMobileMenu },
          ])}
        >
          <a
            href=""
            onClick={(event) => {
              event.preventDefault();
              setActiveMobileMenu(false);
            }}
            className="mt-5 ml-5"
          >
            <Lucide icon="X" className="w-8 h-8 text-white" />
          </a>
        </div>
        <div
          className={clsx([
            "h-full box bg-white/[0.95] rounded-none xl:rounded-xl z-20 relative w-[275px] duration-300 transition-[width] group-[.side-menu--collapsed]:xl:w-[91px] group-[.side-menu--collapsed.side-menu--on-hover]:xl:shadow-[6px_0_12px_-4px_#0000000f] group-[.side-menu--collapsed.side-menu--on-hover]:xl:w-[275px] overflow-hidden flex flex-col",
          ])}
          onMouseOver={(event) => {
            event.preventDefault();
            setCompactMenuOnHover(true);
          }}
          onMouseLeave={(event) => {
            event.preventDefault();
            setCompactMenuOnHover(false);
          }}
        >
          <div
            className={clsx([
              "flex-none hidden xl:flex items-center z-10 px-5 h-[65px] w-[275px] overflow-hidden relative duration-300 group-[.side-menu--collapsed]:xl:w-[91px] group-[.side-menu--collapsed.side-menu--on-hover]:xl:w-[275px]",
            ])}
          >
            <a
              href=""
              className="flex items-center transition-[margin] duration-300 group-[.side-menu--collapsed]:xl:ml-2 group-[.side-menu--collapsed.side-menu--on-hover]:xl:ml-0"
            >
              <div className="flex items-center justify-center w-[34px] rounded-lg h-[34px] bg-gradient-to-b from-theme-1 to-theme-2/80 transition-transform ease-in-out group-[.side-menu--collapsed.side-menu--on-hover]:xl:-rotate-180">
                <div className="w-[16px] h-[16px] relative -rotate-45 [&_div]:bg-white">
                  <div className="absolute w-[21%] left-0 inset-y-0 my-auto rounded-full opacity-50 h-[75%]"></div>
                  <div className="absolute w-[21%] inset-0 m-auto h-[120%] rounded-full"></div>
                  <div className="absolute w-[21%] right-0 inset-y-0 my-auto rounded-full opacity-50 h-[75%]"></div>
                </div>
              </div>
              <div className="ml-3.5 group-[.side-menu--collapsed.side-menu--on-hover]:xl:opacity-100 group-[.side-menu--collapsed]:xl:opacity-0 transition-opacity font-medium">
                APSALES
              </div>
            </a>
            <a
              href=""
              onClick={toggleCompactMenu}
              className="hidden group-[.side-menu--collapsed.side-menu--on-hover]:xl:opacity-100 group-[.side-menu--collapsed]:xl:rotate-180 group-[.side-menu--collapsed]:xl:opacity-0 transition-[opacity,transform] 3xl:flex items-center justify-center w-[20px] h-[20px] ml-auto border rounded-full border-slate-600/40 hover:bg-slate-600/5 dark:border-darkmode-100"
            >
              <Lucide icon="ArrowLeft" className="w-3.5 h-3.5 stroke-[1.3]" />
            </a>
          </div>
          <div
            ref={scrollableRef}
            className={clsx([
              "w-full h-full z-20 px-5 overflow-y-auto overflow-x-hidden pb-3 [-webkit-mask-image:-webkit-linear-gradient(top,rgba(0,0,0,0),black_30px)] [&:-webkit-scrollbar]:w-0 [&:-webkit-scrollbar]:bg-transparent",
              "[&_.simplebar-content]:p-0 [&_.simplebar-track.simplebar-vertical]:w-[10px] [&_.simplebar-track.simplebar-vertical]:mr-0.5 [&_.simplebar-track.simplebar-vertical_.simplebar-scrollbar]:before:bg-slate-400/30",
            ])}
          >
            <ul className="scrollable">
              {/* BEGIN: First Child */}
              {formattedMenu.map((menu, menuKey) =>
                typeof menu == "string" ? (
                  <li className="side-menu__divider" key={menuKey}>
                    {menu}
                  </li>
                ) : (
                  <li key={menuKey}>
                    <a
                      href=""
                      className={clsx([
                        "side-menu__link",
                        { "side-menu__link--active": menu.active },
                        {
                          "side-menu__link--active-dropdown":
                            menu.activeDropdown,
                        },
                        { "opacity-50 cursor-not-allowed": menu.ignore },
                      ])}
                      onClick={(event: React.MouseEvent) => {
                        event.preventDefault();
                        if (menu.ignore) return;
                        linkTo(menu, navigate, true, location.search);
                        setFormattedMenu([...formattedMenu]);
                      }}
                    >
                      <Lucide
                        icon={menu.icon}
                        className="side-menu__link__icon"
                      />
                      <div className="side-menu__link__title">{menu.title}</div>
                      {menu.badge && (
                        <div className="side-menu__link__badge">
                          {menu.badge}
                        </div>
                      )}
                      {menu.subMenu && (
                        <Lucide
                          icon="ChevronDown"
                          className="side-menu__link__chevron"
                        />
                      )}
                    </a>
                    {/* BEGIN: Second Child */}
                    {menu.subMenu && (
                      <Transition
                        in={menu.activeDropdown}
                        onEnter={enter}
                        onExit={leave}
                        timeout={300}
                      >
                        <ul
                          className={clsx([
                            "",
                            { block: menu.activeDropdown },
                            { hidden: !menu.activeDropdown },
                          ])}
                        >
                          {menu.subMenu.map((subMenu, subMenuKey) => (
                            <li key={subMenuKey}>
                              <a
                                href=""
                                className={clsx([
                                  "side-menu__link",
                                  { "side-menu__link--active": subMenu.active },
                                  {
                                    "side-menu__link--active-dropdown":
                                      subMenu.activeDropdown,
                                  },
                                  { "opacity-50 cursor-not-allowed": subMenu.ignore },
                                ])}
                                onClick={(event: React.MouseEvent) => {
                                  event.preventDefault();
                                  if (subMenu.ignore) return;
                                  linkTo(subMenu, navigate, true, location.search);
                                  setFormattedMenu([...formattedMenu]);
                                }}
                              >
                                <Lucide
                                  icon={subMenu.icon}
                                  className="side-menu__link__icon"
                                />
                                <div className="side-menu__link__title">
                                  {subMenu.title}
                                </div>
                                {subMenu.badge && (
                                  <div className="side-menu__link__badge">
                                    {subMenu.badge}
                                  </div>
                                )}
                                {subMenu.subMenu && (
                                  <Lucide
                                    icon="ChevronDown"
                                    className="side-menu__link__chevron"
                                  />
                                )}
                              </a>
                              {/* BEGIN: Third Child */}
                              {subMenu.subMenu && (
                                <Transition
                                  in={subMenu.activeDropdown}
                                  onEnter={enter}
                                  onExit={leave}
                                  timeout={300}
                                >
                                  <ul
                                    className={clsx([
                                      "",
                                      {
                                        block: subMenu.activeDropdown,
                                      },
                                      { hidden: !subMenu.activeDropdown },
                                    ])}
                                  >
                                    {subMenu.subMenu.map(
                                      (lastSubMenu, lastSubMenuKey) => (
                                        <li key={lastSubMenuKey}>
                                          <a
                                            href=""
                                            className={clsx([
                                              "side-menu__link",
                                              {
                                                "side-menu__link--active":
                                                  lastSubMenu.active,
                                              },
                                              {
                                                "side-menu__link--active-dropdown":
                                                  lastSubMenu.activeDropdown,
                                              },
                                              "group-[.side-menu--collapsed]:xl:py-2 group-[.side-menu--collapsed]:xl:px-2 group-[.side-menu--collapsed]:xl:mb-0",
                                              "group-[.side-menu--collapsed.side-menu--on-hover]:xl:py-3 group-[.side-menu--collapsed.side-menu--on-hover]:xl:px-[14px] group-[.side-menu--collapsed.side-menu--on-hover]:xl:mb-1",
                                              { "opacity-50 cursor-not-allowed": lastSubMenu.ignore },
                                            ])}
                                            onClick={(
                                              event: React.MouseEvent
                                            ) => {
                                              event.preventDefault();
                                              if (lastSubMenu.ignore) return;
                                              linkTo(lastSubMenu, navigate, true, location.search);
                                              setFormattedMenu([
                                                ...formattedMenu,
                                              ]);
                                            }}
                                          >
                                            <Lucide
                                              icon={lastSubMenu.icon}
                                              className="side-menu__link__icon"
                                            />
                                            <div className="side-menu__link__title">
                                              {lastSubMenu.title}
                                            </div>
                                            {lastSubMenu.badge && (
                                              <div className="side-menu__link__badge">
                                                {lastSubMenu.badge}
                                              </div>
                                            )}
                                          </a>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </Transition>
                              )}
                              {/* END: Third Child */}
                            </li>
                          ))}
                        </ul>
                      </Transition>
                    )}
                    {/* END: Second Child */}
                  </li>
                )
              )}
              {/* END: First Child */}
            </ul>
          </div>
        </div>
      </div>
    )}
    
    {/* TOP BAR - Always visible */}
    <div className="fixed h-[65px] transition-[margin] duration-100 xl:ml-[275px] group-[.side-menu--collapsed]:xl:ml-[90px] mt-3.5 inset-x-0 top-0 z-[60]">
      <div
        className={clsx([
          "top-bar absolute left-0 xl:left-3.5 right-0 h-full mx-5 group",
          "before:content-[''] before:absolute before:top-0 before:inset-x-0 before:-mt-[15px] before:h-[20px] before:backdrop-blur before:-z-[1]",
          topBarActive && "top-bar--active",
        ])}
      >
        <div
          className="
            container relative flex items-center w-full h-full transition-[padding,background-color,border-color] ease-in-out duration-300 box bg-transparent border-transparent shadow-none dark:bg-transparent dark:border-transparent
            group-[.top-bar--active]:box group-[.top-bar--active]:px-5
            group-[.top-bar--active]:bg-transparent group-[.top-bar--active]:border-transparent group-[.top-bar--active]:bg-gradient-to-r group-[.top-bar--active]:from-theme-1 group-[.top-bar--active]:to-theme-2
          "
        >
          <div className="flex items-center gap-1 xl:hidden">
            <a
              href=""
              onClick={(event) => {
                event.preventDefault();
                setActiveMobileMenu(true);
              }}
              className="p-2 text-white rounded-full hover:bg-white/5"
            >
              <Lucide icon="AlignJustify" className="w-[18px] h-[18px]" />
            </a>
          </div>
            {/* BEGIN: Breadcrumb */}
            <Breadcrumb light className="flex-1 hidden xl:block">
              {breadcrumbs.map((crumb, index) => (
                <Breadcrumb.Link
                  key={index}
                  className="dark:before:bg-chevron-white"
                  to={crumb.path}
                  active={crumb.active}
                >
                  {crumb.label}
                </Breadcrumb.Link>
              ))}
            </Breadcrumb>
            {/* END: Breadcrumb */}
          {/* BEGIN: Notification & User Menu */}
          <div className="flex items-center flex-1">
            <div className="flex items-center gap-1 ml-auto">
              <button
                type="button"
                className="p-2 text-white rounded-full hover:bg-white/5 cursor-pointer"
                onClick={() => setActivitiesPanel(true)}
              >
                <Lucide icon="LayoutGrid" className="w-[18px] h-[18px]" />
              </button>
              <button
                type="button"
                className="p-2 text-white rounded-full hover:bg-white/5 cursor-pointer"
                onClick={() => requestFullscreen()}
              >
                <Lucide icon="Expand" className="w-[18px] h-[18px]" />
              </button>
              <button
                type="button"
                className="p-2 text-white rounded-full hover:bg-white/5 cursor-pointer"
                onClick={() => setNotificationsPanel(true)}
              >
                <Lucide icon="Bell" className="w-[18px] h-[18px]" />
              </button>
            </div>
            <Menu as="div" className="ml-5 relative">
              <Menu.Button className="overflow-hidden rounded-full w-[36px] h-[36px] border-[3px] border-white/[0.15] bg-white/10 flex items-center justify-center cursor-pointer">
                <span className="text-white text-xs font-semibold">
                  {userInitials}
                </span>
              </Menu.Button>
              <Menu.Items className="w-56 mt-1">
                <Menu.Item
                  onClick={async () => {
                    try {
                      // Call logout API if needed
                      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                      await fetch(`${apiUrl}/auth/logout`, {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                      });
                    } catch (error) {
                      console.error('Logout API error:', error);
                    } finally {
                      // Clear all auth data
                      localStorage.removeItem("token");
                      localStorage.removeItem("role");
                      sessionStorage.clear();

                      // Redirect to login
                      navigate("/login");
                    }
                  }}
                >
                  <Lucide icon="Power" className="w-4 h-4 mr-2" />
                  Logout
                </Menu.Item>
              </Menu.Items>
            </Menu>
          </div>
          <ActivitiesPanel
            activitiesPanel={activitiesPanel}
            setActivitiesPanel={setActivitiesPanel}
          />
          <NotificationsPanel
            notificationsPanel={notificationsPanel}
            setNotificationsPanel={setNotificationsPanel}
          />
          {/* SwitchAccount component disabled - faker profiles removed */}
          {/* END: Notification & User Menu */}
        </div>
      </div>
    </div>

    <div
        className={clsx([
          "transition-[margin,width] duration-100 xl:pl-3.5 pt-[54px] pb-16 relative z-10 group mode",
          !hideSideMenuOnClients && { "xl:ml-[275px]": !compactMenu },
          !hideSideMenuOnClients && { "xl:ml-[91px]": compactMenu },
          { "mode--light": !topBarActive },
        ])}
      >

        <div className="px-5 mt-16">
          <div className="container">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Main;
